import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Device } from "mediasoup-client";
import { releaseMediaStream, useMediasoupSignaling } from "./ThitdTest";

const SIGNALING_URL = process.env.REACT_APP_SIGNALING_URL || "wss://apps.pluglr.com/ws/stream";

const LiveRadioHost = () => {
  const [hostName, setHostName] = useState("");
  const [roomId, setRoomId] = useState("");
  const [joinedRoom, setJoinedRoom] = useState(false);
  const [micActive, setMicActive] = useState(false);
  const [playlist, setPlaylist] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(null);
  const [remoteSpeakers, setRemoteSpeakers] = useState([]);
  const [status, setStatus] = useState("Idle");
  const deviceRef = useRef(null);
  const sendTransportRef = useRef(null);
  const recvTransportRef = useRef(null);
  const producersRef = useRef({ mic: null, music: null });
  const consumersRef = useRef(new Map());
  const micStreamRef = useRef(null);
  const musicStreamRef = useRef(null);
  const audioElementRef = useRef(null);
  const signaling = useMediasoupSignaling(SIGNALING_URL, null);

  const safeRoomId = useMemo(() => roomId.trim(), [roomId]);

  const updateStatus = useCallback((message) => {
    setStatus(message);
  }, []);

  const ensureDevice = useCallback(async () => {
    if (deviceRef.current) {
      return deviceRef.current;
    }
    const { routerRtpCapabilities } = await signaling.request("getRouterRtpCapabilities", { roomId: safeRoomId });
    const device = new Device();
    await device.load({ routerRtpCapabilities });
    deviceRef.current = device;
    return device;
  }, [safeRoomId, signaling]);

  const connectTransport = useCallback(
    async (transportId, dtlsParameters) => {
      await signaling.request("connectTransport", {
        roomId: safeRoomId,
        transportId,
        dtlsParameters,
      });
    },
    [safeRoomId, signaling]
  );

  const ensureSendTransport = useCallback(async () => {
    if (sendTransportRef.current) {
      return sendTransportRef.current;
    }
    const device = await ensureDevice();
    const transportOptions = await signaling.request("createTransport", {
      roomId: safeRoomId,
      direction: "send",
    });
    const transport = device.createSendTransport(transportOptions);
    transport.on("connect", async ({ dtlsParameters }, success, fail) => {
      try {
        await connectTransport(transportOptions.id, dtlsParameters);
        success();
      } catch (error) {
        fail(error);
      }
    });
    transport.on("produce", async ({ kind, rtpParameters, appData }, success, fail) => {
      try {
        const { id } = await signaling.request("produce", {
          roomId: safeRoomId,
          transportId: transportOptions.id,
          kind,
          rtpParameters,
          appData,
        });
        success(id);
      } catch (error) {
        fail(error);
      }
    });
    transport.on("connectionstatechange", (state) => {
      if (state === "connected") {
        updateStatus("Streaming");
      }
      if (state === "failed" || state === "closed") {
        updateStatus("Transport closed");
      }
    });
    sendTransportRef.current = transport;
    return transport;
  }, [connectTransport, ensureDevice, safeRoomId, signaling, updateStatus]);

  const ensureRecvTransport = useCallback(async () => {
    if (recvTransportRef.current) {
      return recvTransportRef.current;
    }
    const device = await ensureDevice();
    const transportOptions = await signaling.request("createTransport", {
      roomId: safeRoomId,
      direction: "receive",
    });
    const transport = device.createRecvTransport(transportOptions);
    transport.on("connect", async ({ dtlsParameters }, success, fail) => {
      try {
        await connectTransport(transportOptions.id, dtlsParameters);
        success();
      } catch (error) {
        fail(error);
      }
    });
    recvTransportRef.current = transport;
    return transport;
  }, [connectTransport, ensureDevice, safeRoomId, signaling]);

  const startMicrophone = useCallback(async () => {
    if (micActive) {
      return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const transport = await ensureSendTransport();
    const [track] = stream.getAudioTracks();
    const producer = await transport.produce({ track, appData: { type: "microphone" } });
    producersRef.current.mic = producer;
    micStreamRef.current = stream;
    setMicActive(true);
    updateStatus("Microphone live");
  }, [ensureSendTransport, micActive, updateStatus]);

  const stopMicrophone = useCallback(() => {
    setMicActive(false);
    if (producersRef.current.mic) {
      producersRef.current.mic.close();
      signaling.request("closeProducer", {
        roomId: safeRoomId,
        producerId: producersRef.current.mic.id,
      });
    }
    producersRef.current.mic = null;
    if (micStreamRef.current) {
      releaseMediaStream(micStreamRef.current);
      micStreamRef.current = null;
    }
  }, [safeRoomId, signaling]);

  const stopMusic = useCallback(() => {
    setCurrentTrackIndex(null);
    if (producersRef.current.music) {
      producersRef.current.music.close();
      signaling.request("closeProducer", {
        roomId: safeRoomId,
        producerId: producersRef.current.music.id,
      });
    }
    producersRef.current.music = null;
    if (musicStreamRef.current) {
      releaseMediaStream(musicStreamRef.current);
      musicStreamRef.current = null;
    }
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.srcObject = null;
      audioElementRef.current.src = "";
    }
  }, [safeRoomId, signaling]);

  const playTrack = useCallback(
    async (index) => {
      if (!playlist[index]) {
        return;
      }
      if (!joinedRoom) {
        return;
      }
      await ensureSendTransport();
      stopMusic();
      const audioElement = audioElementRef.current;
      if (!audioElement) {
        return;
      }
      audioElement.srcObject = null;
      audioElement.src = playlist[index].url;
      await audioElement.play();
      const stream = audioElement.captureStream ? audioElement.captureStream() : audioElement.mozCaptureStream ? audioElement.mozCaptureStream() : null;
      if (!stream) {
        return;
      }
      musicStreamRef.current = stream;
      const [track] = stream.getAudioTracks();
      const transport = await ensureSendTransport();
      const producer = await transport.produce({ track, appData: { type: "music", trackId: playlist[index].id } });
      producersRef.current.music = producer;
      setCurrentTrackIndex(index);
      updateStatus(`Playing ${playlist[index].name}`);
    },
    [ensureSendTransport, joinedRoom, playlist, stopMusic, updateStatus]
  );

  const handlePlaylistSelection = useCallback(
    (event) => {
      const files = Array.from(event.target.files || []);
      playlist.forEach((item) => URL.revokeObjectURL(item.url));
      const items = files.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}`,
        name: file.name,
        url: URL.createObjectURL(file),
      }));
      setPlaylist(items);
      setCurrentTrackIndex(null);
    },
    [playlist]
  );

  const consumeSpeaker = useCallback(
    async ({ producerId, speakerId }) => {
      const device = await ensureDevice();
      const transport = await ensureRecvTransport();
      const { id, kind, rtpParameters } = await signaling.request("consume", {
        roomId: safeRoomId,
        producerId,
        transportId: transport.id,
        rtpCapabilities: device.rtpCapabilities,
      });
      if (kind !== "audio") {
        await signaling.request("resume", { roomId: safeRoomId, consumerId: id });
        return;
      }
      const consumer = await transport.consume({ id, producerId, kind, rtpParameters });
      const stream = new MediaStream([consumer.track]);
      consumersRef.current.set(id, { consumer, speakerId });
      setRemoteSpeakers((prev) => {
        const existing = prev.filter((item) => item.consumerId !== id);
        return [...existing, { consumerId: id, speakerId, stream }];
      });
      await signaling.request("resume", { roomId: safeRoomId, consumerId: id });
    },
    [ensureDevice, ensureRecvTransport, safeRoomId, signaling]
  );

  const stopAll = useCallback(() => {
    stopMusic();
    stopMicrophone();
    consumersRef.current.forEach(({ consumer }) => consumer.close());
    consumersRef.current.clear();
    setRemoteSpeakers([]);
    if (sendTransportRef.current) {
      sendTransportRef.current.close();
      sendTransportRef.current = null;
    }
    if (recvTransportRef.current) {
      recvTransportRef.current.close();
      recvTransportRef.current = null;
    }
    if (deviceRef.current) {
      deviceRef.current = null;
    }
    setJoinedRoom(false);
    updateStatus("Idle");
  }, [stopMicrophone, stopMusic, updateStatus]);

  const handleServerNotification = useCallback(
    async (message) => {
      if (!message) {
        return;
      }
      if (message.type === "room_ready") {
        if (message.roomId) {
          setRoomId(message.roomId);
        }
        setJoinedRoom(true);
        updateStatus("Room ready");
        return;
      }
      if (message.type === "producer_available") {
        await consumeSpeaker({ producerId: message.producerId, speakerId: message.speakerId || message.producerId });
        return;
      }
      if (message.type === "producer_closed") {
        const consumerEntry = Array.from(consumersRef.current.entries()).find(([, value]) => value.speakerId === message.speakerId || value.consumer.producerId === message.producerId);
        if (consumerEntry) {
          const [consumerId, { consumer }] = consumerEntry;
          consumer.close();
          consumersRef.current.delete(consumerId);
          setRemoteSpeakers((prev) => prev.filter((item) => item.consumerId !== consumerId));
        }
        return;
      }
      if (message.type === "room_closed") {
        stopAll();
        return;
      }
    },
    [consumeSpeaker, stopAll, updateStatus]
  );

  useEffect(() => {
    signaling.setHandler(handleServerNotification);
    return () => {
      signaling.setHandler(null);
    };
  }, [handleServerNotification, signaling]);

  useEffect(() => {
    return () => {
      stopAll();
      playlist.forEach((item) => URL.revokeObjectURL(item.url));
    };
  }, [playlist, stopAll]);

  const launchStation = useCallback(async () => {
    if (!hostName.trim()) {
      return;
    }
    if (!signaling.ready) {
      return;
    }
    const response = await signaling.request("hostReady", { name: hostName.trim() });
    const newRoomId = response.roomId || safeRoomId || response.id;
    if (!newRoomId) {
      return;
    }
    setRoomId(newRoomId);
    signaling.sendRaw({ type: "join", role: "host", roomId: newRoomId, name: hostName.trim() });
    setJoinedRoom(true);
    updateStatus("Joining room");
  }, [hostName, safeRoomId, signaling, updateStatus]);

  const requestExistingSpeakers = useCallback(async () => {
    if (!joinedRoom) {
      return;
    }
    const list = await signaling.request("listProducers", { roomId: safeRoomId });
    if (Array.isArray(list)) {
      for (const item of list) {
        await consumeSpeaker({ producerId: item.producerId || item.id, speakerId: item.speakerId || item.id });
      }
    }
  }, [consumeSpeaker, joinedRoom, safeRoomId, signaling]);

  useEffect(() => {
    if (!joinedRoom || !signaling.ready) {
      return;
    }
    requestExistingSpeakers();
  }, [joinedRoom, requestExistingSpeakers, signaling.ready]);

  const toggleMicrophone = useCallback(() => {
    if (micActive) {
      stopMicrophone();
    } else {
      startMicrophone();
    }
  }, [micActive, startMicrophone, stopMicrophone]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "24px" }}>
      <h1>Pluglr Live Radio Host</h1>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span>Host name</span>
          <input value={hostName} onChange={(event) => setHostName(event.target.value)} disabled={joinedRoom} />
        </label>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button onClick={launchStation} disabled={joinedRoom || !hostName.trim() || !signaling.ready}>
            Launch station
          </button>
          <span>Room: {safeRoomId || "–"}</span>
          <span>Status: {status}</span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <button onClick={toggleMicrophone} disabled={!joinedRoom}>
          {micActive ? "Mute microphone" : "Enable microphone"}
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <h2>Playlist</h2>
        <input type="file" accept="audio/*" multiple onChange={handlePlaylistSelection} />
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {playlist.map((item, index) => (
            <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span>{item.name}</span>
              <button onClick={() => playTrack(index)} disabled={!joinedRoom}>
                {currentTrackIndex === index ? "Restart" : "Play"}
              </button>
              {currentTrackIndex === index ? (
                <button onClick={stopMusic}>Stop</button>
              ) : null}
            </div>
          ))}
        </div>
        <audio ref={audioElementRef} hidden controls />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <h2>Remote speakers</h2>
        {remoteSpeakers.length === 0 ? <span>No remote speakers</span> : null}
        {remoteSpeakers.map((speaker) => (
          <div key={speaker.consumerId} style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span>Speaker {speaker.speakerId || speaker.consumerId}</span>
            <audio
              controls
              autoPlay
              ref={(element) => {
                if (element && element.srcObject !== speaker.stream) {
                  element.srcObject = speaker.stream;
                }
              }}
            />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: "12px" }}>
        <button onClick={stopAll} disabled={!joinedRoom}>
          Disconnect
        </button>
      </div>
    </div>
  );
};

export default LiveRadioHost;
